import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Play, CalendarCheck } from "lucide-react";
import { IMAGES } from "@/lib/images";
import { scrollToSection } from "./Nav";

const EASE = [0.22, 1, 0.36, 1];

const MaskedLine = ({ children, delay, className = "" }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className={`block ${className}`}
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 0.9, delay, ease: EASE }}
    >
      {children}
    </motion.span>
  </span>
);

const BADGES = [
  { label: "5 artistes sur scène", testid: "hero-badge-artistes" },
  { label: "100% live", testid: "hero-badge-live" },
  { label: "Rock 70s → aujourd'hui", testid: "hero-badge-rock" },
];

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const fade = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

  return (
    <section
      id="hero"
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-screen flex items-end overflow-hidden"
    >
      <motion.div style={{ y: bgY }} className="absolute inset-0">
        <img
          src="/photos/scene-complete.webp"
          alt="Forty One en concert — le groupe au complet sur scène"
          className="w-full h-[125%] object-cover opacity-55"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#09090D] via-[#09090D]/40 to-[#09090D]/15" />
        <div
          className="absolute inset-0"
          style={{
            background:
              "radial-gradient(ellipse 60% 45% at 70% 20%, rgba(157,78,221,0.28), transparent 65%), radial-gradient(ellipse 45% 35% at 15% 75%, rgba(216,0,166,0.18), transparent 60%)",
          }}
        />
      </motion.div>

      <motion.div style={{ opacity: fade }} className="w-full max-w-7xl mx-auto px-5 sm:px-8 pt-40 pb-20 sm:pb-28">
        <motion.img
          src="/logo.webp"
          alt="Logo Forty One"
          data-testid="hero-logo-image"
          initial={{ opacity: 0, scale: 0.85 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, delay: 0.05, ease: EASE }}
          className="h-20 sm:h-28 w-auto mb-8"
        />
        <motion.p
          data-testid="hero-eyebrow"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.15, ease: EASE }}
          className="text-xs uppercase tracking-[0.25em] font-jbmono font-semibold text-amber-400 mb-6"
        >
          Groupe de reprises rock — pour les collectivités de France
        </motion.p>

        <h1 className="font-display text-6xl sm:text-8xl lg:text-9xl font-extrabold tracking-tight uppercase leading-[0.9]">
          <MaskedLine delay={0.25}>Forty</MaskedLine>
          <MaskedLine delay={0.4} className="text-amber-500">
            One<span className="text-red-500">.</span>
          </MaskedLine>
        </h1>

        <motion.p
          data-testid="hero-tagline"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.7, ease: EASE }}
          className="mt-7 max-w-xl text-base sm:text-lg leading-relaxed text-zinc-300"
        >
          Une chanteuse à la voix exceptionnelle, quatre musiciens et les grands
          classiques du rock — des années 70 à aujourd'hui. Le concert clé en
          main pour vos fêtes locales, festivals et soirées municipales.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.9, ease: EASE }}
          className="mt-9 flex flex-wrap items-center gap-4"
        >
          <button
            data-testid="hero-booking-cta"
            onClick={() => scrollToSection("#booking")}
            className="group flex items-center gap-2.5 bg-amber-500 hover:bg-amber-400 text-white font-bold uppercase tracking-wider text-sm rounded-full px-7 py-4 transition-colors duration-300 glow-amber"
          >
            <CalendarCheck size={17} className="transition-transform duration-300 group-hover:-rotate-6" />
            Demander un concert
          </button>
          <button
            data-testid="hero-teaser-cta"
            onClick={() => scrollToSection("#videos")}
            className="group flex items-center gap-2.5 border border-zinc-600 hover:border-amber-500/60 text-zinc-200 hover:text-amber-400 font-bold uppercase tracking-wider text-sm rounded-full px-7 py-4 transition-colors duration-300"
          >
            <Play size={16} className="transition-transform duration-300 group-hover:scale-110" />
            Voir le teaser
          </button>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 1.1 }}
          className="mt-10 flex flex-wrap gap-2.5"
        >
          {BADGES.map((b) => (
            <span
              key={b.testid}
              data-testid={b.testid}
              className="text-xs font-jbmono uppercase tracking-widest text-zinc-300 border border-amber-500/20 bg-[#161622]/70 backdrop-blur-sm rounded-full px-4 py-2"
            >
              {b.label}
            </span>
          ))}
        </motion.div>
      </motion.div>
    </section>
  );
};

export default Hero;
